import React from "react";
import styled from "styled-components";
import { SPOTTER_STRINGS } from "../strings";
import { GamebookPlay } from "./service";

/**
 * Per-`GamebookPlay`-field render decisions, factored out of `GamebookScreen.tsx` so the Films
 * Ground anchor-review gamebook band (`filmsGround/anchorStage/AnchorGamebookTable.tsx`) can
 * reuse the SAME situation/flags/yardage rendering the Gamebook tab already has, rather than a
 * second, independently-drifting copy. `GamebookScreen.tsx`'s desktop grid and mobile card list
 * both import these from here, unchanged in behavior/output.
 *
 * Each caller supplies its OWN cell wrapper for `.q`/`.dd`/`.yd` styling (styled-components scopes
 * nested selectors to their own ancestor, so a `className="q"` span here only picks up whichever
 * wrapper actually renders around it) — this module owns the CONTENT, not the surrounding grid/
 * column layout, which two very differently-shaped tables (Gamebook's fixed desktop grid vs. the
 * anchor editor's four-column band) each need their own version of. `Fl` (the flag pill) is the
 * one exception: reused verbatim, unrestyled, by both — see `AnchorGamebookTable.tsx`'s own doc
 * comment for why that's a deliberate, documented deviation from the Option A mockup's pill shape
 * rather than an oversight.
 */

const ordDown = (down: string | null): string | null =>
  down
    ? (
        { "1": "1st", "2": "2nd", "3": "3rd", "4": "4th" } as Record<
          string,
          string
        >
      )[down] ?? null
    : null;

export const situationCellContent = (play: GamebookPlay): React.ReactNode => (
  <>
    <span className="q">
      {play.quarter >= 5
        ? SPOTTER_STRINGS.gamebook.toolbar.overtime
        : SPOTTER_STRINGS.gamebook.toolbar.quarter(play.quarter)}
    </span>
    {play.gameClock} ·{" "}
    <span className="dd">
      {ordDown(play.down)
        ? `${ordDown(play.down)} & ${play.distanceToGo}`
        : "—"}
    </span>{" "}
    · {play.lineOfScrimmage}
  </>
);

export const playCellContent = (play: GamebookPlay): React.ReactNode => (
  <>
    {play.yardsGainedOrLost != null && (
      <span
        className={`yd ${
          Number(play.yardsGainedOrLost) > 0
            ? "pos"
            : Number(play.yardsGainedOrLost) < 0
            ? "neg"
            : "zero"
        }`}
      >
        {Number(play.yardsGainedOrLost) > 0 ? "+" : ""}
        {play.yardsGainedOrLost}
      </span>
    )}
    {play.mechanicalSummary || play.offensivePlayElements || ""}
  </>
);

export const isCriticalPlay = (play: GamebookPlay): boolean =>
  play.playImportance?.toLowerCase() === "critical";

/** Flag pill — reused unrestyled by both `GamebookScreen.tsx` and the anchor editor's gamebook
 *  band (see this file's doc comment). */
export const Fl = styled.span<{ $tone: "td" | "pn" | "cr" }>`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 5px;
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
  background: ${(p) =>
    p.$tone === "td"
      ? "var(--spotterOkSoft)"
      : p.$tone === "pn"
      ? "var(--spotterFlagSoft)"
      : "var(--spotterDangerSoft)"};
  color: ${(p) =>
    p.$tone === "td"
      ? "var(--spotterOk)"
      : p.$tone === "pn"
      ? "var(--spotterFlag)"
      : "var(--spotterDanger)"};
`;

export const flagsCellContent = (
  scored: boolean,
  play: GamebookPlay
): React.ReactNode => (
  <>
    {scored && <Fl $tone="td">{SPOTTER_STRINGS.gamebook.scoreFlag}</Fl>}
    {play.penaltyType && (
      <Fl $tone="pn">{SPOTTER_STRINGS.gamebook.penaltyFlag}</Fl>
    )}
    {isCriticalPlay(play) && (
      <Fl $tone="cr">{SPOTTER_STRINGS.gamebook.keyFlag}</Fl>
    )}
  </>
);

/** Whether a play has anything worth showing in a "Flags" field at all — the desktop grid always
 *  renders `FlagsCell` (an empty grid cell costs nothing there), but the mobile card list skips
 *  the whole labeled field when there's nothing to flag rather than showing an empty one. Shares
 *  `isCriticalPlay` with `flagsCellContent` above so the two conditions can't drift apart. */
export const hasAnyFlag = (scored: boolean, play: GamebookPlay): boolean =>
  scored || !!play.penaltyType || isCriticalPlay(play);

/**
 * Positions (in `plays`) of every play that scored — "the score went up on THIS play" — computed
 * once over the game's full, ORDERED play list (score only ever climbs, so a play "scores" when
 * either side's running total is higher than the play immediately before it). Keyed on array
 * INDEX rather than `play.uid`: uid uniqueness within a game has never been verified against real
 * data, and a genuine duplicate uid would falsely flag both rows. `plays` MUST already be in game
 * order — both callers' own data (`GamebookCompletedGame.plays`, `AnchorJob.plays`) already is.
 */
export function computeScoredIndices(plays: GamebookPlay[]): Set<number> {
  const scored = new Set<number>();
  let prevAway = 0;
  let prevHome = 0;
  plays.forEach((play, i) => {
    if (play.awayScore > prevAway || play.homeScore > prevHome) {
      scored.add(i);
    }
    prevAway = play.awayScore;
    prevHome = play.homeScore;
  });
  return scored;
}
